/**
 * useConfigSuggestions — Load dropdown suggestions from ConfigCategory/ConfigItem
 *
 * Fetches items from a ConfigCategory by code, returns string[] of item names.
 * Falls back to hardcoded defaults if DB hasn't been seeded yet.
 *
 * Usage:
 *   const deposits = useConfigSuggestions('DEPOSIT_OPTIONS', ['30%', '50%', '100%']);
 *   <EditableCombobox suggestions={deposits} ... />
 */

import { trpcClient } from '@/lib/trpc';
import { useQuery } from '@tanstack/react-query';

const CACHE_TIME = 5 * 60 * 1000; // 5 min

export function useConfigSuggestions(categoryCode: string, fallback: string[] = []): string[] {
    const { data } = useQuery({
        queryKey: ['config-suggestions', categoryCode],
        queryFn: async () => {
            try {
                const cat = await trpcClient.config.getCategoryWithItems.query({ code: categoryCode });
                if (cat && cat.items && cat.items.length > 0) {
                    const dbItems = cat.items
                        .filter((i: any) => i.active !== false)
                        .map((i: any) => i.name);
                    // Always include fallbacks to prevent standard options from disappearing
                    return Array.from(new Set([...fallback, ...dbItems]));
                }
                return null; // No items found, will use fallback
            } catch {
                return null;
            }
        },
        staleTime: CACHE_TIME,
        gcTime: CACHE_TIME * 2,
    });

    return data ?? fallback;
}
